/**
 * Main Layout component for Web App
 */
import React from 'react';
import axios from 'axios';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../../WebApp';
import ThemeToggle from '../ThemeToggle';

function Layout() {
  const { user, logout, language, changeLanguage, updateUser } = React.useContext(AuthContext);
  const location = useLocation();
  const [logoLeft, setLogoLeft] = React.useState(1);
  const [logoRight, setLogoRight] = React.useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showNicknameModal, setShowNicknameModal] = React.useState(false);
  const [nicknameValue, setNicknameValue] = React.useState('');
  const [nicknameError, setNicknameError] = React.useState('');
  const [nicknameSaving, setNicknameSaving] = React.useState(false);
  const [showAvatarModal, setShowAvatarModal] = React.useState(false);
  const [avatarUrl, setAvatarUrl] = React.useState('');
  const [avatarSaving, setAvatarSaving] = React.useState(false);

  const isActive = (path) => location.pathname === path;
  const isGlobalChat = location.pathname === '/web/global-chat';
  const isFullWidth = location.pathname === '/web/global-chat' || location.pathname.startsWith('/web/forum');

  const logoBasePath = process.env.NODE_ENV === 'production' ? '/web/logo' : '/logo';
  const logoLeftSrc = `${logoBasePath}/${logoLeft}.png`;
  const logoRightSrc = `${logoBasePath}/${logoRight}.png`;

  const handleLogoLeftClick = () => {
    setLogoLeft((prev) => (prev % 6) + 1);
  };

  const handleLogoRightClick = () => {
    setLogoRight((prev) => (prev % 6) + 1);
  };

  const translations = {
    en: {
      explore: 'Explore',
      myChat: 'My Chat',
      chat: 'Chat',
      globalChat: 'Global Chat',
      forum: 'Forum',
      create: 'Create',
      myAI: 'My Girls',
      gallery: 'Gallery',
      prices: 'Prices',
      profile: 'Profile',
      logout: 'Logout',
      tokens: 'Tokens',
      login: 'Login',
      register: 'Register',
      registerWithTokens: 'Get Free Tokens'
    },
    ru: {
      explore: 'Исследовать',
      myChat: 'Мои чаты',
      chat: 'Чат',
      globalChat: 'Общий чат',
      forum: 'Форум',
      create: 'Создать',
      myAI: 'Мои подруги',
      gallery: 'Галерея',
      prices: 'Цены',
      profile: 'Профиль',
      logout: 'Выход',
      tokens: 'Токены',
      login: 'Вход',
      register: 'Регистрация',
      registerWithTokens: 'Получи бесплатные токены'
    }
  };

  const t = translations[language] || translations.en;

  const displayName = user?.nickname || user?.email;

  const openNicknameModal = () => {
    if (!user) return;
    setNicknameValue(user.nickname || '');
    setNicknameError('');
    setShowNicknameModal(true);
  };

  const handleNicknameSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (user.nickname) {
      setShowNicknameModal(false);
      return;
    }
    const value = (nicknameValue || '').trim();
    if (value.length < 3 || value.length > 20) {
      setNicknameError('Ник должен быть от 3 до 20 символов.');
      return;
    }
    setNicknameSaving(true);
    setNicknameError('');
    try {
      const response = await axios.post('/auth/nickname', { nickname: value });
      if (response?.data && updateUser) {
        updateUser({ ...(user || {}), nickname: response.data.nickname || value });
      }
      setShowNicknameModal(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Не удалось сохранить ник.';
      setNicknameError(detail);
    } finally {
      setNicknameSaving(false);
    }
  };

  const handleAvatarSubmit = async (e) => {
    e.preventDefault();
    if (!avatarUrl || avatarSaving) return;
    
    setAvatarSaving(true);
    try {
      const response = await axios.post('/api/update_avatar', { avatar_url: avatarUrl });
      if (response.data.success) {
        updateUser({ ...user, avatar_url: avatarUrl });
        setShowAvatarModal(false);
        setAvatarUrl('');
      }
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to update avatar');
    } finally {
      setAvatarSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 text-white flex flex-col pt-16 sm:pt-20">
      {/* Navigation Bar */}
      <nav className="bg-dark-800 border-b border-burgundy-800 flex-shrink-0 fixed top-0 inset-x-0 z-50">
        <div className="w-full px-3 sm:px-4 lg:px-8">
          <div className="flex items-center py-3 relative">
            {/* Left: primary nav (hidden on mobile, uses mobile menu instead) */}
            <div className="hidden sm:flex items-center gap-2 text-sm sm:text-base">
              <Link
                to="/web/explore"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/explore')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.explore}
              </Link>
              <Link
                to="/web/my-chat"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/my-chat')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.myChat}
              </Link>
              <Link
                to="/web/create"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/create')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.create}
              </Link>
              <Link
                to="/web/characters"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/characters')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.myAI}
              </Link>
              <Link
                to="/web/gallery"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/gallery')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.gallery}
              </Link>
              <Link
                to="/web/global-chat"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/global-chat')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.globalChat}
              </Link>
              <Link
                to="/web/forum"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/forum')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.forum}
              </Link>
              <Link
                to="/web/prices"
                className={`px-3 py-2 rounded-md font-semibold ${
                  isActive('/web/prices')
                    ? 'bg-burgundy-600 text-white'
                    : 'text-gray-200 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {t.prices}
              </Link>
            </div>

            {/* Center: logo with cycling */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
              <button
                type="button"
                onClick={handleLogoLeftClick}
                aria-label="Change left logo"
                className="focus:outline-none"
              >
                <img
                  src={logoLeftSrc}
                  alt="HotBump left logo"
                  className="h-10 w-10 sm:h-14 sm:w-14 object-contain"
                />
              </button>
              <Link
                to="/web/explore"
                className="text-lg sm:text-2xl font-extrabold hover:text-burgundy-400 transition-colors"
              >
                HotBump
              </Link>
              <button
                type="button"
                onClick={handleLogoRightClick}
                aria-label="Change right logo"
                className="focus:outline-none"
              >
                <img
                  src={logoRightSrc}
                  alt="HotBump right logo"
                  className="h-10 w-10 sm:h-14 sm:w-14 object-contain"
                />
              </button>
            </div>

            {/* Right cluster aligned right */}
            <div className="hidden sm:flex items-center gap-2 text-sm sm:text-base ml-auto">
              {user && (
                <Link
                  to="/web/promo"
                  className="px-3 py-2 rounded-md font-semibold text-gray-200 hover:bg-dark-700 hover:text-white"
                >
                  {t.tokens}: {user.token_balance || 0}
                </Link>
              )}

              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="bg-dark-700 border border-burgundy-800 text-white px-2 py-2 rounded-md text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-burgundy-600"
              >
                <option value="en">EN</option>
                <option value="ru">RU</option>
              </select>
              
              {/* Theme Toggle */}
              <ThemeToggle inline={true} />

              {user && (
                <>
                  <button
                    type="button"
                    onClick={openNicknameModal}
                    className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-dark-700 hover:bg-dark-600 transition"
                    title={user.nickname ? (language === 'en' ? 'Nickname is locked' : 'Ник зафиксирован') : (language === 'en' ? 'Set nickname (one time)' : 'Установите ник (единожды)')}
                  >
                    {displayName}
                  </button>
                  
                  {/* Avatar Upload Button */}
                  <button
                    type="button"
                    onClick={() => setShowAvatarModal(true)}
                    className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-burgundy-700 hover:bg-burgundy-600 transition flex items-center gap-2"
                    title={language === 'en' ? 'Change avatar' : 'Изменить аватар'}
                  >
                    <span>🖼️</span>
                    <span className="hidden lg:inline\">{language === 'en' ? 'Avatar' : 'Аватар'}</span>
                  </button>
                </>
              )}

              <a 
                href="https://t.me/hotbump_bot?startapp=start" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-md font-semibold text-gray-200 hover:text-burgundy-400 hover:bg-dark-700 transition"
              >
                {language === 'en' ? 'In Telegram' : 'В Telegram'}
              </a>

              {user ? (
                <button
                  onClick={logout}
                  className="px-3 py-2 rounded-md font-semibold text-gray-200 hover:bg-dark-700 hover:text-white"
                >
                  {t.logout}
                </button>
              ) : (
                <>
                  <Link
                    to="/web/login"
                    className="px-3 py-2 rounded-md font-semibold text-gray-200 hover:bg-dark-700 hover:text-white"
                  >
                    {t.login}
                  </Link>
                  <Link
                    to="/web/register"
                    className="px-3 py-2 rounded-md font-semibold bg-burgundy-600 hover:bg-burgundy-700 text-white"
                  >
                    {t.registerWithTokens}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="sm:hidden ml-auto px-4 py-3 rounded-md font-semibold text-gray-200 hover:bg-dark-700 hover:text-white text-lg"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden px-3 pb-3">
            <div className="bg-dark-800 border border-burgundy-800 rounded-xl p-3 space-y-2">
              <div className="flex flex-wrap gap-2 text-sm">
                <Link to="/web/explore" className="px-3 py-2 rounded-md font-semibold bg-burgundy-600 text-white w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.explore}</Link>
                <Link to="/web/my-chat" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.myChat}</Link>
                <Link to="/web/create" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.create}</Link>
                <Link to="/web/characters" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.myAI}</Link>
                <Link to="/web/gallery" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.gallery}</Link>
                <Link to="/web/global-chat" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.globalChat}</Link>
                <Link to="/web/forum" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.forum}</Link>
                <Link to="/web/prices" className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 w-full text-center" onClick={() => setMobileMenuOpen(false)}>{t.prices}</Link>
              </div>
              {user && (
                <div className="px-3 py-2 rounded-md font-semibold bg-dark-700 text-gray-200 text-center">
                  {t.tokens}: {user.token_balance || 0}
                </div>
              )}
              <div className="flex items-center justify-center">
                <select
                  value={language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="bg-dark-700 border border-burgundy-800 text-white px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-600 w-full"
                >
                  <option value="en">EN</option>
                  <option value="ru">RU</option>
                </select>
              </div>
              {user && (
                <button
                  type="button"
                  onClick={() => { openNicknameModal(); setMobileMenuOpen(false); }}
                  className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-dark-700 text-center hover:bg-dark-600 transition"
                  title={user.nickname ? (language === 'en' ? 'Nickname is locked' : 'Ник зафиксирован') : (language === 'en' ? 'Set nickname (one time)' : 'Установите ник (единожды)')}
                >
                  {displayName}
                </button>
              )}
              <a 
                href="https://t.me/hotbump_bot?startapp=start" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-dark-700 hover:text-burgundy-400 transition block text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'en' ? 'In Telegram' : 'В Telegram'}
              </a>
              {user ? (
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-dark-700 hover:bg-dark-600 w-full"
                >
                  {t.logout}
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/web/login"
                    className="px-3 py-2 rounded-md font-semibold text-gray-200 bg-dark-700 hover:bg-dark-600 text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.login}
                  </Link>
                  <Link
                    to="/web/register"
                    className="px-3 py-2 rounded-md font-semibold bg-burgundy-600 hover:bg-burgundy-700 text-white text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.register}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
        <main className={isFullWidth ? "w-full px-0 py-0 flex-grow" : "max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8 flex-grow"}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-dark-800 border-t border-burgundy-800 mt-auto py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-center">
            <Link
              to="/web/policies"
              className="text-xs sm:text-sm text-gray-400 hover:text-burgundy-400 transition"
            >
              Terms & Policies
            </Link>
          </div>
        </div>
      </footer>

      {showNicknameModal && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-dark-800 border border-burgundy-800 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold">
                  {language === 'en' ? 'Set your nickname' : 'Установите никнейм'}
                </h3>
                <p className="text-sm text-gray-400">
                  {language === 'en'
                    ? 'You can set it only once. After saving, it cannot be changed.'
                    : 'Ник можно ввести только один раз. После сохранения изменить его нельзя.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowNicknameModal(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
                aria-label={language === 'en' ? 'Close' : 'Закрыть'}
              >
                ×
              </button>
            </div>

            {user?.nickname ? (
              <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-gray-200">
                {language === 'en'
                  ? `Your nickname: ${user.nickname}`
                  : `Ваш ник: ${user.nickname}`}
              </div>
            ) : (
              <form onSubmit={handleNicknameSave} className="space-y-3">
                <label className="block text-sm text-gray-300">
                  {language === 'en' ? 'Nickname (3-20 characters)' : 'Ник (3-20 символов)'}
                </label>
                <input
                  autoFocus
                  maxLength={20}
                  value={nicknameValue}
                  onChange={(e) => setNicknameValue(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-dark-700 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                  placeholder={language === 'en' ? 'Enter nickname' : 'Введите ник'}
                />
                {nicknameError && (
                  <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-lg">
                    {nicknameError}
                  </div>
                )}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowNicknameModal(false)}
                    className="px-4 py-2 rounded-lg border border-white/20 text-gray-200 hover:bg-white/5"
                  >
                    {language === 'en' ? 'Cancel' : 'Отмена'}
                  </button>
                  <button
                    type="submit"
                    disabled={nicknameSaving}
                    className="px-5 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold disabled:opacity-60"
                  >
                    {nicknameSaving
                      ? (language === 'en' ? 'Saving...' : 'Сохранение...')
                      : (language === 'en' ? 'Save nickname' : 'Сохранить ник')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      
      {/* Avatar Upload Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-dark-800 rounded-lg p-8 max-w-md w-full border border-dark-700">
            <h3 className="text-2xl font-bold text-white mb-6">
              {language === 'en' ? 'Change Avatar' : 'Изменить аватар'}
            </h3>
            <form onSubmit={handleAvatarSubmit}>
              <div className="mb-6">
                <label className="block text-white mb-2 font-semibold">
                  {language === 'en' ? 'Avatar URL:' : 'URL аватара:'}
                </label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none"
                  required
                />
                <p className="text-xs text-gray-400 mt-2">
                  {language === 'en' 
                    ? 'Enter full image URL (PNG, JPG, GIF)'
                    : 'Введите полный URL изображения (PNG, JPG, GIF)'}
                </p>
              </div>
              
              {avatarUrl && (
                <div className="mb-6">
                  <p className="text-white text-sm mb-2">
                    {language === 'en' ? 'Preview:' : 'Предпросмотр:'}
                  </p>
                  <img 
                    src={avatarUrl} 
                    alt="Avatar preview" 
                    className="w-24 h-24 rounded-full mx-auto object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAvatarModal(false);
                    setAvatarUrl('');
                  }}
                  className="flex-1 px-4 py-3 bg-dark-700 text-white rounded hover:bg-dark-600 transition font-semibold"
                >
                  {language === 'en' ? 'Cancel' : 'Отмена'}
                </button>
                <button
                  type="submit"
                  disabled={avatarSaving || !avatarUrl}
                  className="flex-1 px-4 py-3 bg-burgundy-600 text-white rounded hover:bg-burgundy-500 transition font-semibold disabled:opacity-50"
                >
                  {avatarSaving 
                    ? (language === 'en' ? 'Saving...' : 'Сохранение...') 
                    : (language === 'en' ? 'Save' : 'Сохранить')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;

